import React, { useState } from 'react';
import PageTitle from '../../components/PageTitle';
import PdfModal from '../../components/PdfModal';
import {
    instrumentationAutomationProductsPageData,
    electricalProductsPageData,
    wirelessProductsPageData,
    laboratoryProductsPageData,
    displayProductsPageData,
    microwaveMoistureSensorsProductsPageData,
    xrayProductsPageData,
    nirProductsPageData
    // Import other product page data as needed
} from '../../data/pageData'; // Assuming these are all in the same 'pageData' file

// Map of category slugs to their respective data
const productCategories = {
    'instrumentation': instrumentationAutomationProductsPageData,
    'electrical': electricalProductsPageData,
    'wireless': wirelessProductsPageData,
    'laboratory': laboratoryProductsPageData,
    'display': displayProductsPageData,
    'microwave-moisture-sensors': microwaveMoistureSensorsProductsPageData,
    'x-ray': xrayProductsPageData,
    'nir': nirProductsPageData
    // Add other categories as needed
};

const ProductPage = ({ category }) => {
    const data = productCategories[category];
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState(null);

    // If category doesn't exist, you might want to handle this case
    if (!data) {
        return (
            <div className="bg-white text-gray-800">
                <PageTitle title="Page Not Found" breadcrumbs="Home > Products" />
                <div className="container mx-auto px-4 py-16 text-center">
                    <h2 className="text-2xl font-semibold mb-4">Product Category Not Found</h2>
                    <p className="text-gray-600">The requested product category does not exist.</p>
                </div>
            </div>
        );
    }

    const handleView = (pdfUrl) => {
        setSelectedPdf(pdfUrl);
        setModalOpen(true);
    };

    const handleDownload = (pdfUrl, fileName) => {
        // Create a temporary link element to trigger download
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName || 'document.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPdf(null);
    };

    return (
        <div className="bg-white text-gray-800">
            <PageTitle title={data.pageTitle} breadcrumbs={data.breadcrumbs} />
            <section className="py-16 md:py-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto text-lg leading-relaxed">{data.introText}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {data.instruments.map((instrument, index) => (
                            <div 
                                key={instrument.name} 
                                className="bg-gray-50 rounded-xl shadow-xl overflow-hidden flex flex-col group transform hover:scale-105 transition-all duration-300 border border-gray-200 hover:border-yellow-500"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className="relative overflow-hidden h-52">
                                    <img src={instrument.img} alt={instrument.name} className="w-full h-full object-cover transition-transform duration-500"/>
                                </div>
                                <div className="p-6 flex flex-col flex-grow">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{instrument.name}</h3>
                                    <p className="text-gray-600 text-sm mb-4 flex-grow leading-relaxed">{instrument.desc}</p>
                                    <div className="mt-auto flex gap-2">
                                        <button
                                            onClick={() => handleView(instrument.pdfUrl)}
                                            className="text-center text-yellow-600 hover:text-yellow-700 font-semibold py-2 px-4 rounded-md text-sm transition-colors duration-300 border border-yellow-500 hover:bg-yellow-50 flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            View
                                        </button>
                                        <button
                                            onClick={() => handleDownload(instrument.pdfUrl, `${instrument.name}.pdf`)}
                                            className="text-center bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-md text-sm transition-colors duration-300 flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Download
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <PdfModal isOpen={modalOpen} onClose={handleCloseModal} pdfUrl={selectedPdf} />
        </div>
    );
};

export default ProductPage; 